import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";
import { useEscape } from "./hooks";

export interface ModalProps {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  /** 取消在左，确认在右，确认按钮写动作本身。给"取消"加 data-autofocus 可让打开时焦点落在它上面。 */
  footer: ReactNode;
  onClose: () => void;
  size?: "lg";
}

/**
 * 二次确认用它，表单走 Drawer；只读的详情查看也可以用 size="lg"。高度不超过屏幕减 64px，超出时正文滚动。
 * 打开时焦点移到带 data-autofocus 的元素（没有则是对话框本身），标题作为对话框的无障碍名称；
 * 关闭时焦点回到打开前的元素（它已被移除时不处理，由调用方移交焦点）。Esc 关闭；焦点在对话框里时 Esc 不冒泡，
 * 不会连带关掉外层的抽屉（它们在 document 上监听，同 Tooltip、Dropdown）。
 */
export function Modal({ open, title, children, footer, onClose, size }: ModalProps) {
  useEscape(open, onClose);
  const titleId = useId(), box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const back = document.activeElement as HTMLElement | null;
    (box.current?.querySelector<HTMLElement>("[data-autofocus]") ?? box.current)?.focus();
    return () => { if (back?.isConnected) back.focus(); };
  }, [open]);
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    e.stopPropagation(); // React 的监听先于 document 上的原生监听
    onClose();
  };
  if (!open) return null;
  return createPortal(
    <>
      <div className="ax-backdrop" onMouseDown={onClose} />
      <div className="ax-modal-center">
        <div ref={box} className={cx("ax-modal", size === "lg" && "ax-modal-lg")} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={onKeyDown}>
          <div className="ax-modal-head" id={titleId}>{title}</div>
          <div className="ax-modal-body">{children}</div>
          <div className="ax-modal-foot">{footer}</div>
        </div>
      </div>
    </>,
    document.body,
  );
}
